import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { TextField } from '@/components/TextField';
import { VideoPlayer } from '@/components/VideoPlayer';
import { VideoTrimmer } from '@/components/VideoTrimmer';
import type { FullSource } from '@/types/source';
import { RangeOrigin } from '@/types/video-player';
import { getTimeDomain } from '@/utils/event-playback-range';
import type { AddCustomEventModalFormValues } from './addCustomEventModalFormValues';

interface VideoTrimStepProps {
    source: FullSource;
    trimStart: number;
    trimEnd: number;
    onTrimChange: (start: number, end: number) => void;
}

export function VideoTrimStep({
    source,
    trimStart,
    trimEnd,
    onTrimChange,
}: VideoTrimStepProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const {
        register,
        clearErrors,
        formState: { errors },
    } = useFormContext<AddCustomEventModalFormValues>();
    const [currentPlaybackTime, setCurrentPlaybackTime] = useState(trimStart);

    const duration = source.duration || 0;
    const videoSrc = source.src || '';

    return (
        <div className="flex flex-col gap-6 p-6">
            <div className="flex flex-col gap-3">
                <VideoPlayer
                    src={videoSrc}
                    source={source}
                    startTime={trimStart}
                    eventRange={{
                        domain: getTimeDomain(source.type),
                        origin: RangeOrigin.SELECTION,
                        start: trimStart,
                        end: trimEnd,
                        playbackStart: trimStart,
                    }}
                    onTimeUpdate={setCurrentPlaybackTime}
                />
                <VideoTrimmer
                    src={videoSrc}
                    duration={duration}
                    trimStart={trimStart}
                    trimEnd={trimEnd}
                    onTrimChange={onTrimChange}
                    currentPlaybackTime={currentPlaybackTime}
                />
            </div>
            <TextField
                label={t('detail.addCustomEventModal.eventNameLabel')}
                placeholder={t('detail.addCustomEventModal.eventNameLabel')}
                {...register('promptText', {
                    required: true,
                    validate: (value) => !!value.trim(),
                    onChange: () => clearErrors('promptText'),
                })}
                error={errors.promptText?.message}
                required
                data-testid="add-custom-event-prompt-input"
            />
            <TextField
                variant="area"
                label={t('detail.addCustomEventModal.descriptionLabel')}
                placeholder={t(
                    'detail.addCustomEventModal.descriptionPlaceholder',
                )}
                {...register('description', {
                    onChange: () => clearErrors('description'),
                })}
                error={errors.description?.message}
                data-testid="add-custom-event-description-input"
            />
        </div>
    );
}
